import { followPaginationLinks } from '@ctw/shared/api/auth-service/pagination';
import type { CTWRequestContext } from '@ctw/shared/context/ctw-context';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import { getZusApiBaseUrl } from '@ctw/shared/utils/environments';
import { QUERY_KEY_ACCESSIBLE_BUILDERS } from '@ctw/shared/utils/query-keys';
import { isEmpty } from 'lodash-es';

export interface Builder {
  id: string;
  type: 'auth/builders';
  attributes: {
    name: string;
    namespace?: string;
    canImpersonate?: boolean;
    billingPackage?: string;
    createdAt?: number;
    updatedAt?: number;
  };
}

interface ListBuildersResponse {
  data: Array<{
    id: string;
    type: 'auth/builders';
    attributes: Partial<Builder['attributes']>;
  }>;
  links?: { next?: string; prev?: string; self: string };
}

// Returns every builder the current user has access to: their own builder, any
// builders they reach via cross-builder grants, and (for Zus-staff users) the
// builders enumerated by their super-org. Use this anywhere we need to decide
// whether a request scoped to a specific builder is safe to make.
//
// On any failure (the auth service may not be reachable from embedded contexts,
// or the user may not have the scope) the hook falls back to a single-entry
// list containing the request-context builder so consumers always see a usable
// result.
export const useAccessibleBuilders = () =>
  useCtwQuery<Array<Builder>>({
    queryId: QUERY_KEY_ACCESSIBLE_BUILDERS,
    // The list of builders a user has access to changes infrequently and is
    // expensive to keep refetching; let it live for a while.
    staleTime: 1000 * 60 * 20,
    gcTime: 1000 * 60 * 20,
    queryFn: async ({ requestContext, ctwFetch, telemetry, signal }) => {
      const params = new URLSearchParams();
      params.set('sort', 'name');
      params.set('page[count]', '500');
      params.set('isSuperOrg', 'false');
      const initialUrl = `${getZusApiBaseUrl(requestContext.env)}/auth/builders?${params}`;

      try {
        const builders = await followPaginationLinks<Builder>({
          initialUrl,
          signal,
          telemetry,
          operationId: 'auth/builders',
          fetchPage: async (url, pageSignal) => {
            const response: { ok: boolean; data: ListBuildersResponse } = await ctwFetch(url, {
              headers: {
                Authorization: `Bearer ${requestContext.authToken}`,
                'Content-Type': 'application/vnd.api+json',
              },
              signal: pageSignal,
            });

            if (!(response.ok && Array.isArray(response.data.data))) {
              if (pageSignal?.aborted) {
                throw new DOMException('Aborted', 'AbortError');
              }
              throw new Error('Failed to list accessible builders');
            }

            return {
              items: response.data.data.map((builder) => ({
                id: builder.id,
                type: 'auth/builders' as const,
                attributes: {
                  ...builder.attributes,
                  name: isEmpty(builder.attributes.name)
                    ? 'Unnamed Builder'
                    : (builder.attributes.name as string),
                },
              })),
              nextUrl: response.data.links?.next,
            };
          },
        });

        return builders.length > 0 ? builders : ownBuilderFallback(requestContext);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          throw error;
        }
        return ownBuilderFallback(requestContext);
      }
    },
  });

const ownBuilderFallback = (requestContext: CTWRequestContext): Array<Builder> => [
  {
    id: requestContext.builderId,
    type: 'auth/builders',
    attributes: {
      name: requestContext.authTokenState.builderName || 'Unnamed Builder',
    },
  },
];
